// src/pages/EventDetail.js — One event: details, attached files (the agenda
// first), topics and attendees.
// Attendees are CRM contacts and firms (linked to their profiles, where the
// event shows up in return; see EventsAttended.js), plus people from a
// pasted list who aren't in the CRM yet, each in a group (LP, GP...).
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { EntitySearch } from '../components/EntityPicker'
import { PeekLink } from '../components/Peek'
import { dateRange, MatchLine } from '../lib/searchTypes'
import { EventModal, EventForm, TopicInput, useTagOptions } from './Events'

const errText = err => err?.response?.data?.error || err?.message

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
      {children}
    </div>
  )
}

// Pick a CRM contact or firm: the shared EntitySearch, so a missing one can
// be created on the spot.
function RecordPicker({ records, idKey, exclude, onPick, submitting, error, initialQuery = '', createDefaults }) {
  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ padding: '8px 10px', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <EntitySearch kind={idKey === 'firm_id' ? 'firm' : 'contact'} records={records} exclude={exclude}
          inline initialQuery={initialQuery} placeholder="Type a name" createDefaults={createDefaults}
          onPick={(id, record) => { if (id && !submitting) onPick(record) }} />
      </div>
      {error && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{error}</div>}
    </div>
  )
}


const DOCUMENT_KINDS = ['Agenda', 'Attendee list', 'Other']

function formatSize(n) {
  if (!n) return ''
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`
}

// Attached files. The agenda carries the programming, panels and panelists.
function Documents({ eventId, documents, onChange }) {
  const [kind, setKind] = useState('Agenda')
  const [file, setFile] = useState(null)
  const [inputKey, setInputKey] = useState(0)

  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('kind', kind)
      return api.post(`/api/events/${eventId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => { setFile(null); setInputKey(k => k + 1); onChange() },
  })
  const remove = useMutation({
    mutationFn: doc => api.delete(`/api/events/${eventId}/documents/${doc.document_id}`),
    onSuccess: onChange,
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <SectionLabel>Agenda &amp; files</SectionLabel>
      {documents.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {documents.map(d => (
            <div key={d.document_id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <span className={`badge ${d.kind === 'Agenda' ? 'badge-blue' : 'badge-gray'}`}>{d.kind}</span>
              {d.signed_url
                ? <a href={d.signed_url} target="_blank" rel="noreferrer" style={{ color: 'var(--blue-text)', fontWeight: 500 }}>{d.file_name}</a>
                : <span>{d.file_name}</span>}
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatSize(d.file_size)}</span>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                disabled={remove.isPending} onClick={() => remove.mutate(d)}>Remove</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
          No agenda attached yet. Attach it below (PDF, Word, anything up to 50 MB).
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={kind} onChange={e => setKind(e.target.value)}>
          {DOCUMENT_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <input key={inputKey} type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} style={{ fontSize: 12 }} />
        <button className="btn btn-primary btn-sm" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
          {upload.isPending ? 'Uploading…' : 'Attach'}
        </button>
      </div>
      {(upload.error || remove.error) && (
        <div style={{ fontSize: 13, color: 'var(--danger-text)', marginTop: 8 }}>{errText(upload.error || remove.error)}</div>
      )}
    </div>
  )
}

const GROUP_OPTIONS = ['LP', 'GP', 'Speaker', 'Sponsor', 'Other']

function PasteList({ documents, groups, onSubmit, onCancel, submitting, error, result }) {
  const [text, setText] = useState('')
  const [group, setGroup] = useState('')
  const lists = documents.filter(d => d.kind !== 'Agenda')
  const [sourceDoc, setSourceDoc] = useState(lists[0]?.document_id ?? '')
  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        One person per line. Paste rows straight from a spreadsheet with columns <code>Name, Firm, Title, Email</code>{' '}
        (combine first and last name into one column), or type <code>Name, Firm, Email</code>. Each line is matched to an
        existing CRM contact (email, or name allowing nicknames when the firm agrees). The rest are kept as "Not in CRM"
        with any near matches suggested. Nothing creates new contacts.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="input-label">Group for these rows</label>
          <input className="input" list="attendee-groups" value={group} onChange={e => setGroup(e.target.value)} placeholder="LP, GP, Speaker..." />
          <datalist id="attendee-groups">{groups.map(g => <option key={g} value={g} />)}</datalist>
        </div>
        <div>
          <label className="input-label">From file</label>
          <select className="select" value={sourceDoc} onChange={e => setSourceDoc(e.target.value)}>
            <option value="">None</option>
            {documents.map(d => <option key={d.document_id} value={d.document_id}>{d.file_name}</option>)}
          </select>
        </div>
      </div>
      <textarea className="textarea" rows={12} autoFocus value={text} onChange={e => setText(e.target.value)}
        placeholder={'Jane Smith, Example Capital, jane@example.com\nJohn Doe, Sample Partners'} />
      {result && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Added {result.added} ({result.matched} matched to CRM contacts, {result.unmatched} not in CRM)
          {result.skipped ? `; skipped ${result.skipped} already on the list` : ''}.
        </div>
      )}
      {error && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>{result ? 'Done' : 'Cancel'}</button>
        <button type="button" className="btn btn-primary" disabled={submitting || !text.trim()}
          onClick={() => onSubmit({ text, attendee_group: group.trim() || null, source_document_id: sourceDoc || null }, () => setText(''))}>
          {submitting ? 'Importing…' : 'Import'}
        </button>
      </div>
    </div>
  )
}

// Display values for an attendee row, whether linked or pasted.
const rowName  = a => a.contact?.name || a.firm?.name || a.name || ''
const rowFirm  = a => a.firm ? a.firm.name : (a.contact?.primary_firm?.name || a.firm_name || '')
const rowTitle = a => a.contact?.title_role || a.title || ''
const rowStatus = a => a.kind === 'person' ? 'Not in CRM' : a.kind === 'firm' ? 'Firm' : 'In CRM'

const SORTS = {
  name:   (a, b) => rowName(a).localeCompare(rowName(b)),
  firm:   (a, b) => rowFirm(a).localeCompare(rowFirm(b)) || SORTS.name(a, b),
  title:  (a, b) => rowTitle(a).localeCompare(rowTitle(b)) || SORTS.name(a, b),
  group:  (a, b) => (a.attendee_group || '~').localeCompare(b.attendee_group || '~') || SORTS.name(a, b),
  status: (a, b) => rowStatus(a).localeCompare(rowStatus(b)) || SORTS.name(a, b),
}

// Click toggles A-Z / Z-A. Inactive columns show ⇅ so it's clear they sort.
function SortTh({ col, sort, setSort, children }) {
  const active = sort.col === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      title={active ? (sort.dir === 'asc' ? 'Sorted A-Z; click for Z-A' : 'Sorted Z-A; click for A-Z') : 'Click to sort A-Z'}
      onClick={() => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }))}>
      {children}{' '}
      <span style={{ opacity: active ? 0.9 : 0.35, fontSize: 11 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  )
}

const DEFAULT_VIEW = { group: 'all', status: 'all', text: '', sort: { col: 'name', dir: 'asc' } }

// The attendee table's filters, search and sort, held by the page so the
// RESET button can sit next to the section title.
function useAttendeeView() {
  const [view, setView] = useState(DEFAULT_VIEW)
  const set = key => value => setView(v => ({ ...v, [key]: typeof value === 'function' ? value(v[key]) : value }))
  const dirty = view.group !== 'all' || view.status !== 'all' || view.text.trim() !== '' ||
    view.sort.col !== DEFAULT_VIEW.sort.col || view.sort.dir !== DEFAULT_VIEW.sort.dir
  return {
    ...view, dirty,
    setGroup: set('group'), setStatus: set('status'), setText: set('text'), setSort: set('sort'),
    reset: () => setView(DEFAULT_VIEW),
  }
}

// The attendee table: group and status filters, sortable columns, and a
// search over the attendees with universal search's matching (names,
// fields, concepts, and everything on a linked contact's or firm's
// profile), applied on top of the filters.
function AttendeeTable({ view, eventId, attendees, documents, onLink, onRemove, onSetGroup, removing, linking, onOpenLink }) {
  const { group, setGroup, status, setStatus, text, setText, sort, setSort } = view
  const [term, setTerm] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 250)
    return () => clearTimeout(t)
  }, [text])

  const { data: hits, isFetching } = useQuery({
    queryKey: ['attendee-search', eventId, term],
    queryFn: () => api.get(`/api/events/${eventId}/attendees/search`, { params: { q: term } }).then(r => r.data.data ?? []),
    enabled: term.length >= 2,
    placeholderData: prev => prev,
  })
  const hitMap = useMemo(() => new Map((hits ?? []).map(h => [h.attendee_id, h])), [hits])
  const searching = term.length >= 2

  const groups = useMemo(() => {
    const counts = new Map()
    for (const a of attendees) counts.set(a.attendee_group || '', (counts.get(a.attendee_group || '') ?? 0) + 1)
    return [...counts].sort(([a], [b]) => (a || '~').localeCompare(b || '~'))
  }, [attendees])
  const docName = useMemo(() => new Map(documents.map(d => [d.document_id, d.file_name])), [documents])

  const rows = useMemo(() => {
    let list = attendees
      .filter(a => group === 'all' || (a.attendee_group || '') === group)
      .filter(a => status === 'all' || rowStatus(a) === status)
    if (searching) list = list.filter(a => hitMap.has(a.attendee_id))
    const byRelevance = searching && sort.col === 'relevance'
    const fn = byRelevance ? (a, b) => hitMap.get(b.attendee_id).score - hitMap.get(a.attendee_id).score : SORTS[sort.col] ?? SORTS.name
    const sorted = [...list].sort(fn)
    return sort.dir === 'desc' && !byRelevance ? sorted.reverse() : sorted
  }, [attendees, group, status, searching, hitMap, sort])

  // Searching sorts by relevance until a column is clicked; clearing the
  // search goes back to name order.
  useEffect(() => {
    setSort(s => searching ? { col: 'relevance', dir: 'desc' } : s.col === 'relevance' ? DEFAULT_VIEW.sort : s)
  }, [searching]) // eslint-disable-line react-hooks/exhaustive-deps

  const chip = (active, onClick, label) => (
    <button type="button" onClick={onClick} className={`btn btn-sm ${active ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
  )

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input className="input" type="search" style={{ maxWidth: 320 }} value={text} onChange={e => setText(e.target.value)}
          placeholder="Search attendees (name, firm, title, concept...)" aria-label="Search attendees" />
        {isFetching && <div className="spinner" />}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>Group:</span>
        {chip(group === 'all', () => setGroup('all'), `All (${attendees.length})`)}
        {groups.map(([g, n]) => chip(group === g, () => setGroup(g), `${g || 'No group'} (${n})`))}
        <select className="select" style={{ maxWidth: 150, fontSize: 12 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="all">Any status</option>
          <option value="In CRM">In CRM</option>
          <option value="Not in CRM">Not in CRM</option>
          <option value="Firm">Firms</option>
        </select>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Showing {rows.length} of {attendees.length}{searching ? ` matching "${term}"` : ''}
        {searching && sort.col !== 'relevance' && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSort({ col: 'relevance', dir: 'desc' })}>Sort by relevance</button>
        )}
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <SortTh col="name" sort={sort} setSort={setSort}>Name</SortTh>
              <SortTh col="firm" sort={sort} setSort={setSort}>Firm</SortTh>
              <SortTh col="title" sort={sort} setSort={setSort}>Title</SortTh>
              <SortTh col="group" sort={sort} setSort={setSort}>Group</SortTh>
              <SortTh col="status" sort={sort} setSort={setSort}>Status</SortTh>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a => {
              const c = a.contact
              const f = a.firm
              const firmLink = c?.primary_firm
              const hit = hitMap.get(a.attendee_id)
              return (
                <tr key={a.attendee_id}>
                  <td style={{ fontWeight: 500, minWidth: 180 }}>
                    {c ? <PeekLink type="contact" id={c.contact_id}>{c.name}</PeekLink>
                      : f ? <PeekLink type="firm" id={f.firm_id}>{f.name}</PeekLink>
                      : a.name}
                    {searching && hit && <MatchLine match={hit.match} />}
                    {a.kind === 'person' && a.suggestions?.length > 0 && (
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginTop: 3 }}>
                        Possible match{a.suggestions.length > 1 ? 'es' : ''} in CRM:{' '}
                        {a.suggestions.map((sg, i) => (
                          <span key={sg.contact_id}>
                            {i > 0 && ' · '}
                            <PeekLink type="contact" id={sg.contact_id}>{sg.name}</PeekLink>
                            {sg.firm ? ` (${sg.firm})` : ' (no firm)'}{' '}
                            <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }}
                              disabled={linking} onClick={() => onLink(a, sg)}>Link</button>
                          </span>
                        ))}
                        {a.suggestions.length > 1 && ' (duplicate contacts; consider merging)'}
                      </div>
                    )}
                  </td>
                  <td>
                    {f ? <span className="badge badge-green">Firm</span>
                      : firmLink ? <PeekLink type="firm" id={firmLink.firm_id}>{firmLink.name}</PeekLink>
                      : (a.firm_name || '—')}
                  </td>
                  <td>{rowTitle(a) || '—'}</td>
                  <td title={a.source_document_id ? `From ${docName.get(a.source_document_id) ?? 'an uploaded list'}` : undefined}>
                    <select className="select" style={{ fontSize: 12, padding: '2px 6px', maxWidth: 110 }}
                      value={a.attendee_group || ''} onChange={e => onSetGroup(a, e.target.value || null)}>
                      <option value="">—</option>
                      {[...new Set([...GROUP_OPTIONS, ...groups.map(([g]) => g).filter(Boolean)])].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </td>
                  <td>
                    {a.kind === 'person'
                      ? <span className="badge badge-yellow">Not in CRM</span>
                      : <span className="badge badge-blue">{a.kind === 'firm' ? 'Firm' : 'In CRM'}</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {a.kind === 'person' && (
                      <button className="btn btn-ghost btn-sm" onClick={() => onOpenLink(a)}>Link</button>
                    )}
                    <button className="btn btn-ghost btn-sm" disabled={removing} onClick={() => onRemove(a)}>Remove</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!rows.length && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>No attendees match.</div>
      )}
    </>
  )
}

export default function EventDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [modal, setModal] = useState(null)        // 'edit' | 'contact' | 'firm' | 'paste' | 'link'
  // The attendee being linked. Kept apart from `modal`: a string has a
  // built-in .link method, so `modal?.link` was truthy for every dialog.
  const [linking, setLinking] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [importResult, setImportResult] = useState(null)

  const { data: ev, isLoading, error } = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get(`/api/events/${id}`).then(r => r.data.data),
  })
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
    enabled: modal === 'contact' || modal === 'link',
  })
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
    enabled: modal === 'firm',
  })

  const refresh = () => {
    for (const key of ['event', 'events', 'events-attended', 'attendee-search']) {
      qc.invalidateQueries({ queryKey: [key] })
    }
  }
  const done = () => { refresh(); setModal(null) }

  const update = useMutation({ mutationFn: payload => api.patch(`/api/events/${id}`, payload), onSuccess: done })
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/events/${id}`),
    onSuccess: () => { refresh(); navigate('/events') },
  })
  const addAttendee = useMutation({ mutationFn: body => api.post(`/api/events/${id}/attendees`, body), onSuccess: done })
  const importList = useMutation({
    mutationFn: body => api.post(`/api/events/${id}/attendees/import`, body).then(r => r.data.data),
    onSuccess: res => { refresh(); setImportResult(res) },
  })
  const linkAttendee = useMutation({
    mutationFn: ({ attendee, contact }) => api.patch(`/api/events/${id}/attendees/${attendee.attendee_id}`, { contact_id: contact.contact_id }),
    onSuccess: done,
  })
  const rematch = useMutation({
    mutationFn: () => api.post(`/api/events/${id}/attendees/rematch`).then(r => r.data.data),
    onSuccess: refresh,
  })
  const removeAttendee = useMutation({
    mutationFn: a => api.delete(`/api/events/${id}/attendees/${a.attendee_id}`),
    onSuccess: refresh,
  })
  const attendeeView = useAttendeeView()
  const tagOptions = useTagOptions()
  // Tags edit in place on the page: each add or × saves immediately.
  const saveTags = useMutation({
    mutationFn: topics => api.patch(`/api/events/${id}`, { topics }),
    onMutate: topics => qc.setQueryData(['event', id], prev => prev && { ...prev, topics }),
    onSettled: refresh,
  })
  const setGroup = useMutation({
    mutationFn: ({ a, group }) => api.patch(`/api/events/${id}/attendees/${a.attendee_id}`, { attendee_group: group }),
    onSuccess: refresh,
  })

  const onListContacts = useMemo(() => new Set((ev?.attendees ?? []).map(a => a.contact_id).filter(Boolean)), [ev])
  const onListFirms = useMemo(() => new Set((ev?.attendees ?? []).map(a => a.firm_id).filter(Boolean)), [ev])

  if (isLoading) return <div className="page"><div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div></div>
  if (error) return (
    <div className="page"><div className="empty-state" style={{ paddingTop: 60 }}>
      <h3>Could not load this event</h3><p>{errText(error)}</p>
      <Link to="/events" className="btn btn-secondary" style={{ marginTop: 12 }}>Back to Events</Link>
    </div></div>
  )

  const attendees = ev.attendees ?? []
  const people = attendees.filter(a => a.kind !== 'firm')
  const firms = attendees.filter(a => a.kind === 'firm')

  const reset = () => { addAttendee.reset(); linkAttendee.reset(); importList.reset(); setImportResult(null) }

  return (
    <div className="page">
      <div style={{ marginBottom: 12 }}>
        <Link to="/events" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>← Events</Link>
      </div>

      <div className="page-header">
        <div className="page-header-text">
          <h1>{ev.name}</h1>
          <p style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="badge badge-blue">{ev.event_type}</span>
            {ev.organizer && <span>Organized by {ev.organizer_firm_id
              ? <PeekLink type="firm" id={ev.organizer_firm_id}>{ev.organizer}</PeekLink>
              : ev.organizer} ·</span>}
            <span>{dateRange(ev.start_date, ev.end_date)}</span>
            {ev.website && (
              <a href={/^https?:\/\//i.test(ev.website) ? ev.website : `https://${ev.website}`}
                target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Website ↗</a>
            )}
          </p>
          {ev.location && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
              {ev.location}
            </div>
          )}
          <div style={{ marginTop: 10, maxWidth: 720 }}>
            <TopicInput topics={ev.topics ?? []} suggestions={tagOptions}
              onChange={topics => saveTags.mutate(topics)} />
            {saveTags.error && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 4 }}>{errText(saveTags.error)}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {confirmDelete ? (
            <>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                Delete this event, its files and its attendee list? Contacts and firms are not touched.
              </span>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(true)}>Delete</button>
              <button className="btn btn-primary" onClick={() => { update.reset(); setModal('edit') }}>Edit</button>
            </>
          )}
        </div>
      </div>

      <Documents eventId={id} documents={ev.documents ?? []} onChange={refresh} />

      {ev.notes && (
        <div className="card" style={{ marginBottom: 20 }}>
          <SectionLabel>Notes</SectionLabel>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{ev.notes}</div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <SectionLabel>
              Attendees ({people.length} {people.length === 1 ? 'person' : 'people'}, {firms.length} firm{firms.length === 1 ? '' : 's'})
            </SectionLabel>
            {attendeeView.dirty && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={attendeeView.reset}
                title="Clear search, filters and sorting"
                style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 8px' }}>
                Reset
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={rematch.isPending} onClick={() => rematch.mutate()}
              title="Link unlinked attendees to contacts that exist in the CRM now">
              {rematch.isPending ? 'Matching…' : rematch.data ? `Re-matched (${rematch.data.matched} linked)` : 'Re-match to CRM'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { reset(); setModal('paste') }}>Paste list</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { reset(); setModal('firm') }}>+ Add firm</button>
            <button className="btn btn-primary btn-sm" onClick={() => { reset(); setModal('contact') }}>+ Add contact</button>
          </div>
        </div>

        {!attendees.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No attendees yet. Add CRM contacts or firms, or paste the attendee list.
          </div>
        ) : (
          <AttendeeTable
            view={attendeeView} eventId={id} attendees={attendees} documents={ev.documents ?? []}
            onLink={(a, contact) => linkAttendee.mutate({ attendee: a, contact })} linking={linkAttendee.isPending}
            onOpenLink={a => { reset(); setLinking(a); setModal('link') }}
            onRemove={a => removeAttendee.mutate(a)} removing={removeAttendee.isPending}
            onSetGroup={(a, group) => setGroup.mutate({ a, group })} />
        )}
        {(removeAttendee.error || setGroup.error) && (
          <div style={{ fontSize: 13, color: 'var(--danger-text)', marginTop: 8 }}>{errText(removeAttendee.error || setGroup.error)}</div>
        )}
      </div>

      {modal === 'edit' && (
        <EventModal title={`Edit - ${ev.name}`} onClose={() => setModal(null)}>
          <EventForm initial={ev} onSubmit={update.mutate} onCancel={() => setModal(null)}
            submitting={update.isPending} error={errText(update.error)} />
        </EventModal>
      )}
      {modal === 'contact' && (
        <EventModal title="Add a CRM contact" onClose={() => setModal(null)}>
          <RecordPicker records={contactsData ?? []} idKey="contact_id" exclude={onListContacts}
            onPick={c => addAttendee.mutate({ contact_id: c.contact_id })}
            submitting={addAttendee.isPending} error={errText(addAttendee.error)}
/>
        </EventModal>
      )}
      {modal === 'firm' && (
        <EventModal title="Add a CRM firm" onClose={() => setModal(null)}>
          <RecordPicker records={firmsData ?? []} idKey="firm_id" exclude={onListFirms}
            onPick={f => addAttendee.mutate({ firm_id: f.firm_id })}
            submitting={addAttendee.isPending} error={errText(addAttendee.error)}
/>
        </EventModal>
      )}
      {modal === 'paste' && (
        <EventModal title="Paste attendee list" onClose={() => setModal(null)}>
          <PasteList documents={ev.documents ?? []}
            groups={[...new Set([...GROUP_OPTIONS, ...attendees.map(a => a.attendee_group).filter(Boolean)])]}
            onSubmit={(body, clear) => importList.mutate(body, { onSuccess: clear })}
            onCancel={() => setModal(null)}
            submitting={importList.isPending} error={errText(importList.error)} result={importResult} />
        </EventModal>
      )}
      {modal === 'link' && linking && (
        <EventModal title={`Link ${linking.name} to a CRM contact`} onClose={() => setModal(null)}>
          <RecordPicker records={contactsData ?? []} idKey="contact_id" exclude={onListContacts}
            initialQuery={linking.name}
            onPick={c => linkAttendee.mutate({ attendee: linking, contact: c })}
            submitting={linkAttendee.isPending} error={errText(linkAttendee.error)}
            createDefaults={{ email: linking.email, title_role: linking.title }} />
        </EventModal>
      )}
    </div>
  )
}
